import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setStatusMsg('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setStatus('loading');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ag26081gss20ku91ig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: formData.get('email') as string,
        }).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMsg('Thank you for subscribing!');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setStatus('error');
        setStatusMsg(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setStatusMsg('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-foreground-900 text-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h4 className="text-xl font-heading font-bold mb-4">Superbuyspread</h4>
            <p className="text-foreground-300 text-sm leading-relaxed mb-4">
              The ultimate Superbuy Spreadsheet resource hub. Discover organized product guides, curated links, and expert shopping tips — all in one place.
            </p>
            <p className="text-foreground-400 text-xs">
              &copy; {new Date().getFullYear()} Superbuyspread. All rights reserved.
            </p>
          </div>

          <div>
            <h5 className="text-xs font-semibold tracking-[0.15em] text-foreground-400 mb-5 font-label uppercase">Newsletter</h5>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-transparent border-b border-foreground-600 pb-2.5 text-sm text-background-50 placeholder:text-foreground-500 outline-none focus:border-accent-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="absolute right-0 top-0 text-foreground-400 hover:text-background-50 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
              <div className="visually-hidden-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              {status !== 'idle' && (
                <p className={`text-xs ${status === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                  {statusMsg}
                </p>
              )}
              <p className="text-foreground-500 text-xs">
                Get the latest <strong>Superbuy Spreadsheet</strong> updates and curated finds delivered to your inbox.
              </p>
            </form>
          </div>

          <div>
            <h5 className="text-xs font-semibold tracking-[0.15em] text-foreground-400 mb-5 font-label uppercase">Categories</h5>
            <ul className="space-y-2.5">
              {categories.slice(0, 6).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="text-xs font-semibold tracking-[0.15em] text-foreground-400 mb-5 font-label uppercase">Quick Links</h5>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">Home</Link></li>
              <li><Link to="/superbuy-spreadsheet-guide" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">Complete Guide</Link></li>
              <li><Link to="/best-superbuy-spreadsheet" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">Best Finds 2026</Link></li>
              <li><Link to="/superbuy-spreadsheet-shoes" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">Shoes Guide</Link></li>
              {categories.slice(6, 9).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                >
                  Visit Main Platform
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-foreground-700/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center">
            <span className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-background-50/10 tracking-tighter select-none">
              SUPERBUY
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}